// ── speech — произношение, одно на всё приложение ────────────────────────────
// Сайт и программа для компьютера: синтез речи браузера (Web Speech API).
// Приложение на Android: в его WebView браузерного синтеза нет (кнопки молчали) —
// там говорит системная озвучка Android через плагин TextToSpeech (с APK 1.4.0).
//   speak(text, 'ja', { rate, voiceURI })  → Promise, выполнится, когда договорит;
//                       язык '' — угадать по буквам (перевод карточки)
//   stopSpeaking()
//   canSpeak()            — есть ли чем говорить на этом устройстве
//   listVoices('ja')      — голоса для выбора (только браузер)

import { Capacitor } from '@capacitor/core';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { notify } from './ui/Toaster';
import { t } from './i18n';

export const LANG_BCP47 = {
  en: 'en-US', fr: 'fr-FR', ja: 'ja-JP', de: 'de-DE', es: 'es-ES',
  it: 'it-IT', ru: 'ru-RU', zh: 'zh-CN', ko: 'ko-KR', pt: 'pt-BR',
  pl: 'pl-PL', tr: 'tr-TR', ar: 'ar-SA', nl: 'nl-NL', sv: 'sv-SE',
  no: 'nb-NO', da: 'da-DK', fi: 'fi-FI', cs: 'cs-CZ', uk: 'uk-UA',
  ro: 'ro-RO', hu: 'hu-HU', el: 'el-GR', he: 'he-IL', fa: 'fa-IR',
  hi: 'hi-IN', bn: 'bn-BD', th: 'th-TH', vi: 'vi-VN', id: 'id-ID',
};

// 'ja', 'ja-JP', 'JA' → 'ja-JP'; неизвестное — как есть.
export const toBcp47 = (lang) => {
  const l = String(lang || '').trim();
  if (!l) return '';
  if (l.includes('-')) return l;
  return LANG_BCP47[l.toLowerCase()] || l;
};

// Язык не известен (перевод карточки) — угадываем по буквам.
export function guessLang(text) {
  const s = String(text || '');
  if (/[ぁ-ゟァ-ヿ]/.test(s)) return 'ja-JP';
  if (/[가-힣ᄀ-ᇿ]/.test(s)) return 'ko-KR';
  if (/[一-鿿㐀-䶿]/.test(s)) return 'zh-CN';
  if (/[؀-ۿ]/.test(s)) return 'ar-SA';
  if (/[Ѐ-ӿ]/.test(s)) return 'ru-RU';
  if (/[àâæçéèêëîïôœùûüÿÀÂÆÇÉÈÊËÎÏÔŒÙÛÜŸ]/.test(s)) return 'fr-FR';
  if (/[äöüÄÖÜß]/.test(s)) return 'de-DE';
  if (/[áéíóúüñÁÉÍÓÚÜÑ]/.test(s)) return 'es-ES';
  return '';
}

const native = () => Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('TextToSpeech');
const web = () => typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.SpeechSynthesisUtterance === 'function';

export const canSpeak = () => native() || web();

export function listVoices(lang) {
  if (native() || !web()) return [];
  const prefix = toBcp47(lang).split('-')[0];
  return window.speechSynthesis.getVoices().filter((v) => v.lang.startsWith(prefix));
}

// Голоса в браузере приходят не сразу — подписка на их появление.
export function onVoicesChanged(fn) {
  if (native() || !web()) return () => {};
  window.speechSynthesis.addEventListener?.('voiceschanged', fn);
  return () => window.speechSynthesis.removeEventListener?.('voiceschanged', fn);
}

let warned = false;
const warnOnce = (text) => { if (!warned) { warned = true; notify(text, { tone: 'error' }); } };

export function stopSpeaking() {
  if (native()) TextToSpeech.stop().catch(() => {});
  else if (web()) window.speechSynthesis.cancel();
}

export async function speak(text, lang, { rate = 0.9, voiceURI = '' } = {}) {
  const say = String(text || '').trim();
  if (!say || say === '—') return;
  const bcp = toBcp47(lang) || guessLang(say) || 'en-US';
  if (native()) {
    try {
      await TextToSpeech.stop().catch(() => {});
      await TextToSpeech.speak({ text: say, lang: bcp, rate, pitch: 1, volume: 1, category: 'playback' });
    } catch (e) {
      const { supported } = await TextToSpeech.isLanguageSupported({ lang: bcp }).catch(() => ({ supported: true }));
      if (!supported) warnOnce(t("На телефоне нет голоса для этого языка. Его можно добавить в настройках Android: «Синтез речи»."));
      else console.warn('[speech]', e?.message || e);
    }
    return;
  }
  if (!web()) {
    warnOnce(Capacitor.isNativePlatform()
      ? t("Озвучка появится после обновления приложения.")
      : t("Этот браузер не умеет произносить слова."));
    return;
  }
  const synth = window.speechSynthesis;
  synth.cancel();
  const u = new window.SpeechSynthesisUtterance(say);
  u.lang = bcp;
  u.rate = rate;
  const voices = synth.getVoices();
  const voice = (voiceURI && voices.find((v) => v.voiceURI === voiceURI))
    || voices.find((v) => v.lang === bcp) || voices.find((v) => v.lang.startsWith(bcp.split('-')[0]));
  if (voice) u.voice = voice;
  await new Promise((resolve) => {
    u.onend = resolve;
    u.onerror = resolve;
    synth.speak(u);
  });
}
